import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    Grid,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PendingIcon from '@mui/icons-material/Pending';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey[100]}`,
    marginTop: theme.spacing(2),
    overflow: 'hidden',
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.grey[700],
    fontWeight: 600,
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    fontSize: '0.875rem',
}));

const BodyCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    color: theme.palette.grey[800],
    fontSize: '0.875rem',
}));

const NotificationChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[700],
    fontWeight: 500,
    height: 28,
    borderRadius: 14,
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
    height: 24,
    fontWeight: 500,
    borderRadius: 12,
    fontSize: '0.75rem',
    backgroundColor: status === 'Active' ? theme.palette.success.light : theme.palette.grey[200],
    color: status === 'Active' ? theme.palette.success.dark : theme.palette.grey[700],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
    },
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
    color: theme.palette.grey[700],
    fontSize: '0.875rem',
}));

const ProjectTable = ({ projects }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const handleRowClick = (projectId, projectName) => {
        // Store project name in sessionStorage for retrieval in App.js
        sessionStorage.setItem(`project_${projectId}_name`, projectName);
        navigate(`/projects/${projectId}`);
    };

    // Display cards on mobile, table on desktop
    if (isMobile) {
        return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {projects.map((project) => (
                    <Grid item xs={12} key={project.id}>
                        <ProjectCard onClick={() => handleRowClick(project.id, project.name)}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        {project.name}
                                    </Typography>
                                    {project.fieldId && (
                                        <Chip
                                            label={project.fieldId}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '24px',
                                                borderColor: '#e0e0e0',
                                                color: '#666',
                                                backgroundColor: '#f8f9fa',
                                            }}
                                        />
                                    )}
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <InfoRow>
                                            <NotificationsIcon fontSize="small" />
                                            <Typography component="span">
                                                {project.notifications} Notifications
                                            </Typography>
                                        </InfoRow>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <InfoRow>
                                            <PendingIcon fontSize="small" />
                                            <Typography component="span">
                                                {project.pending} Pending
                                            </Typography>
                                        </InfoRow>
                                    </Grid>
                                </Grid>

                                <InfoRow sx={{ mt: 1 }}>
                                    <CalendarTodayIcon fontSize="small" />
                                    <Typography component="span">{project.startDate}</Typography>
                                </InfoRow>

                                <Box sx={{ mt: 2 }}>
                                    <StatusChip
                                        label={project.status}
                                        status={project.status}
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </ProjectCard>
                    </Grid>
                ))}
            </Grid>
        );
    }

    // Display table on desktop
    return (
        <StyledTableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <HeaderCell>Name</HeaderCell>
                        <HeaderCell>Notifications</HeaderCell>
                        <HeaderCell>Pending</HeaderCell>
                        <HeaderCell>Start date</HeaderCell>
                        <HeaderCell>Status</HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {projects.map((project) => (
                        <TableRow
                            key={project.id}
                            hover
                            onClick={() => handleRowClick(project.id, project.name)}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: theme.palette.grey[50] },
                            }}
                        >
                            <BodyCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontWeight: 500 }}>{project.name}</Typography>
                                    {project.fieldId && (
                                        <Chip
                                            label={project.fieldId}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '20px',
                                                borderColor: '#e0e0e0',
                                                color: '#666',
                                                backgroundColor: '#f8f9fa',
                                            }}
                                        />
                                    )}
                                </Box>
                            </BodyCell>
                            <BodyCell>
                                <NotificationChip label={project.notifications} size="small" />
                            </BodyCell>
                            <BodyCell>
                                <NotificationChip label={project.pending} size="small" />
                            </BodyCell>
                            <BodyCell>
                                <Typography variant="body2">{project.startDate}</Typography>
                            </BodyCell>
                            <BodyCell>
                                <StatusChip
                                    label={project.status}
                                    status={project.status}
                                    size="small"
                                />
                            </BodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </StyledTableContainer>
    );
};

export default ProjectTable;
